import { useEffect, useState } from 'react';
import { RefreshCwIcon, XIcon } from 'lucide-react';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import viewFreshnessCopy, { type ViewFreshnessKey } from '@/i18n/ns/view-freshness';
import overlaysCopy from '@/i18n/ns/overlays';
import { relativeTimeLabel } from './artifact-panels/format';
import { useIntlLocale } from './intl-locale';
import './view-freshness.css';

// A cached live-data view (App, dashboard, living Result) opens at once from its last good data; this is the one quiet
// line in its top bar that says how current it is, with Refresh. The cached content never waits on it and nothing
// covers it while a refresh runs: the words change, the icon turns, the values update in place. A phone bar shows the
// data's compact age beside the icon instead of the sentence. A failure the reader can act on says how in a small note
// under the bar, on a phone too, until they close it.

/** What every surface adapter hands the UI. `fetchedAt` is the actual successful acquisition of the shown view. */
export type ViewFreshnessValue = {
  /** `sync`: an App that syncs a connected source into its own tables; `fetchedAt` is its last verified sync. */
  kind?: 'view' | 'sync';
  fetchedAt: string | null;
  /** `failed`: the last refresh failed and the shown view is still the authorised one. */
  status: 'idle' | 'refreshing' | 'failed';
  /** A failed sync whose outcome is not known (it may have run): the line says it is not confirmed, not that it failed. */
  unconfirmed?: boolean;
  /** Absent when this viewer cannot start a refresh. */
  refresh?: () => void;
  /** What the reader can do about a failed refresh, in their words (e.g. the source needs reconnecting). */
  hint?: string;
};

type Copy = Readonly<Record<ViewFreshnessKey, string>>;
const MINUTE = 60_000;

/** One kind's words: a cached view is updated and refreshed; a synced App is synced, and says so before its first. */
function wordsFor(
  copy: Copy,
  { kind, unconfirmed }: Pick<ViewFreshnessValue, 'kind' | 'unconfirmed'>,
) {
  return kind === 'sync'
    ? {
        updated: copy.synced,
        justNow: copy.syncedJustNow,
        updating: copy.syncing,
        failed: unconfirmed ? copy.syncUnknown : copy.syncFailed,
        refresh: copy.syncNow,
        never: copy.notSynced,
      }
    : {
        updated: copy.updated,
        justNow: copy.justNow,
        updating: copy.updating,
        failed: copy.failed,
        refresh: copy.refresh,
        never: '',
      };
}

/** The line's words for one moment; stale is not a separate state, the age says it. */
export function viewFreshnessText(
  value: Pick<ViewFreshnessValue, 'fetchedAt' | 'status' | 'kind' | 'unconfirmed'>,
  copy: Copy,
  locale: string,
  now = Date.now(),
): string {
  const { fetchedAt, status } = value;
  const words = wordsFor(copy, value);
  if (status === 'refreshing') return words.updating;
  const at = fetchedAt ? new Date(fetchedAt).getTime() : Number.NaN;
  const time = Number.isFinite(at) ? relativeTimeLabel(fetchedAt, locale, now) : null;
  const updated = !time
    ? ''
    : now - at < MINUTE
      ? words.justNow
      : formatCopy(words.updated, { time });
  // A failure right after a fetch is just "Couldn't update": "· Updated just now" beside it would contradict it.
  if (status === 'failed')
    return updated && now - at >= MINUTE ? `${words.failed} · ${updated}` : words.failed;
  return updated || words.never;
}

/** The phone bar's compact age of the shown data: "4 min", "2 hr", "3 days", then the date. Empty when unknown or
 * under a minute old. */
export function viewFreshnessAge(
  { fetchedAt }: Pick<ViewFreshnessValue, 'fetchedAt'>,
  locale: string,
  now = Date.now(),
): string {
  const at = fetchedAt ? new Date(fetchedAt).getTime() : Number.NaN;
  if (!Number.isFinite(at)) return '';
  const elapsed = Math.max(0, now - at);
  // Fresh data needs no age: the caption appears from the first minute.
  if (elapsed < MINUTE) return '';
  const unit = (count: number, name: 'minute' | 'hour' | 'day') =>
    new Intl.NumberFormat(locale, { style: 'unit', unit: name, unitDisplay: 'short' }).format(
      count,
    );
  try {
    if (elapsed < 60 * MINUTE) return unit(Math.floor(elapsed / MINUTE), 'minute');
    if (elapsed < 24 * 60 * MINUTE) return unit(Math.floor(elapsed / (60 * MINUTE)), 'hour');
    if (elapsed < 7 * 24 * 60 * MINUTE)
      return unit(Math.floor(elapsed / (24 * 60 * MINUTE)), 'day');
    return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short' }).format(at);
  } catch {
    return '';
  }
}

/** Re-render each half minute so "4 minutes ago" keeps true without a reload. */
function useNow(): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), MINUTE / 2);
    return () => window.clearInterval(timer);
  }, []);
  return now;
}

export function ViewFreshness({ value }: { value: ViewFreshnessValue }) {
  const copy = useNamespaceCopy(viewFreshnessCopy);
  const overlays = useNamespaceCopy(overlaysCopy);
  const locale = useIntlLocale();
  const now = useNow();
  const text = viewFreshnessText(value, copy, locale, now);
  const words = wordsFor(copy, value);
  // A synced App before its first sync has no age yet: a phone shows a quiet dash in the age's place (the sentence
  // stays the icon's name and for screen readers).
  const age =
    viewFreshnessAge(value, locale, now) ||
    (value.kind === 'sync' && !value.fetchedAt && value.status === 'idle' ? '—' : '');
  const refreshing = value.status === 'refreshing';
  const hint = value.status === 'failed' ? value.hint : undefined;
  // Closed until the next failure: any other status reopens it for then.
  const [closed, setClosed] = useState(false);
  useEffect(() => {
    if (value.status !== 'failed') setClosed(false);
  }, [value.status]);
  if (!text && !value.refresh) return null;
  return (
    <span
      className="ws-view-freshness"
      data-slot="view-freshness"
      data-status={value.status}
      data-unconfirmed={(value.status === 'failed' && value.unconfirmed) || undefined}
      title={hint}
    >
      {text && <small className="ws-view-freshness-text">{text}</small>}
      {/* On a phone the age shows in place of the sentence, which stays for screen readers and the icon's title:
          as a caption under the icon, or beside the line when this reader can't refresh. */}
      {age && !value.refresh && (
        <small className="ws-view-freshness-age" aria-hidden="true">
          {age}
        </small>
      )}
      {value.refresh && (
        <button
          type="button"
          className="ws-icon-button ws-view-freshness-refresh"
          aria-label={words.refresh}
          title={[words.refresh, text, hint].filter(Boolean).join(' · ')}
          disabled={refreshing}
          aria-busy={refreshing || undefined}
          onClick={value.refresh}
        >
          <RefreshCwIcon aria-hidden="true" />
          {age && (
            <small className="ws-view-freshness-age" aria-hidden="true">
              {age}
            </small>
          )}
        </button>
      )}
      {hint && !closed && (
        <span className="ws-view-freshness-hint">
          {/* The status below already says it to screen readers. */}
          <span aria-hidden="true">{hint}</span>
          <button
            type="button"
            className="ws-icon-button ws-view-freshness-hint-close"
            aria-label={overlays.dismiss}
            onClick={() => setClosed(true)}
          >
            <XIcon aria-hidden="true" />
          </button>
        </span>
      )}
      {/* Said once when it happens, not on every minute's new age. */}
      <span className="sr-only" role="status">
        {value.status === 'failed' ? [words.failed, hint].filter(Boolean).join('. ') : ''}
      </span>
    </span>
  );
}
